import { Component, OnInit, AfterViewInit, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormControl, FormArray } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { environment } from '../../../../../environments/environment';

// PrimeNG
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';

// Shared UI & Services
import { PageLayoutComponent } from '../../../../shared/ui/page-layout/page-layout.component';
import { DynamicFormRendererComponent } from '../../../../shared/ui/dynamic-form-renderer/dynamic-form-renderer.component';
import { VerticalStepperComponent } from '../../../../shared/ui/vertical-stepper/vertical-stepper.component';
import { DynamicViewRendererComponent } from '../../../../shared/ui/dynamic-view-renderer/dynamic-view-renderer.component';
import { PageTitleService } from '../../../../services/page-title.service';
import { VALIDATION_MESSAGES } from '../../../../shared/constants/validation.constants';
import { UiOrchestratorService } from '../../../../core/services/ui-orchestrator.service';
import { AppAlertComponent } from '../../../../shared/components/app-alert/app-alert.component';

// Core Services
import { FormConfigAdapterService } from '../../../../core/services/form-config-adapter.service';
import { FormOptionsService } from '../../../../core/services/form-options.service';
import { FormDefaultsService } from '../../../../core/services/form-defaults.service';
import { RegistrationConfigService } from '../../../../core/services/registration-config.service';
import { PermissionService } from '../../../../core/services/permission.service';
import { EmployeeFormPayloadService } from '../../services/employee-form-payload.service';

// Models
import { ModuleFormConfig, StepConfig, FormFieldConfig } from '../../../../shared/models/page-config.model';
import { BackendFormMetadata } from '../../../../shared/models/backend-form-config.model';


@Component({
  selector: 'app-employee-registration-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    TooltipModule,
    PageLayoutComponent,
    DynamicFormRendererComponent,
    VerticalStepperComponent,
    DynamicViewRendererComponent,
    AppAlertComponent
  ],
  templateUrl: './employee-registration-page.component.html'
})
export class EmployeeRegistrationPageComponent implements OnInit, AfterViewInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private pageTitleService = inject(PageTitleService);
  private uiOrchestrator = inject(UiOrchestratorService);
  private http = inject(HttpClient);
  private adapter = inject(FormConfigAdapterService);
  private formOptions = inject(FormOptionsService);
  private formDefaults = inject(FormDefaultsService);
  private registrationConfigService = inject(RegistrationConfigService);
  private platformId = inject(PLATFORM_ID);
  private permissionService = inject(PermissionService);
  private payloadService = inject(EmployeeFormPayloadService);

  private readonly DRAFT_KEY = 'arrow_hr_emp_reg_draft';
  private readonly DRAFT_ID_KEY = 'arrow_hr_emp_reg_draft_id';

  // --- Edit Mode Signals ---
  isEditMode = signal(false);
  employeeId = signal<string | null>(null);
  private get storage(): Storage | null {
    return isPlatformBrowser(this.platformId) ? window.localStorage : null;
  }

  // ─── Loading State ───────────────────────────────────────────────────────────
  isLoading = signal(true);
  loadError = signal<string | null>(null);
  draftEmployeeId = signal<string | null>(null);
  isSavingDraft = signal(false);

  canSaveDraft(): boolean {
    const val = this.employeeForm?.get('full_name')?.value;
    return typeof val === 'string' && val.trim().length > 0;
  }

  // ─── Dynamic Config ──────────────────────────────────────────────────────────
  formConfig = signal<ModuleFormConfig | null>(null);
  private _submitUrl = signal<string>('/api/v1/employees/register');
  private _hiddenFields = signal<string[]>([]);
  private _formMetadata = signal<BackendFormMetadata | null>(null);

  // ─── Active Step State ───────────────────────────────────────────────────────
  activeIndex = signal(0);
  maxVisitedStep = signal(0);

  // ─── Reactive Form ────────────────────────────────────────────────────────────
  employeeForm!: FormGroup;

  // ─── Derived Helpers ─────────────────────────────────────────────────────────
  // Step-level view filtering — a step the current user's IAM policies don't grant
  // view access to must never reach DynamicFormRendererComponent, which only
  // enforces field/section-level policy, not step-level. Only enforced when the
  // step actually declares a view policy: FormConfigAdapterService.transform()
  // always appends a synthetic client-side "Review" step (no backend equivalent,
  // no policies) — that step must stay visible to everyone, so an *undeclared*
  // policy here is permissive, unlike PermissionService.canView()'s canonical
  // deny-by-default (which is correct for backend-declared nodes, not synthetic
  // client-side ones with nothing to gate against).
  get steps(): StepConfig[] {
    return (this.formConfig()?.steps ?? []).filter(step => {
      const viewScope = step.policies?.view;
      return !viewScope || this.permissionService.canView(step);
    });
  }

  get currentStep(): StepConfig {
    return this.steps[this.activeIndex()] ?? { title: '', sections: [] };
  }

  private getCurrentStepFields(): FormFieldConfig[] {
    return this.currentStep.sections.flatMap(s => s.fields);
  }

  private getFieldError(field: FormFieldConfig): string {
    const control = this.employeeForm.get(field.key);
    if (!control || !control.errors) return '';

    const errors = control.errors;
    const errorKeys = Object.keys(errors);
    if (errorKeys.length === 0) return '';

    const firstKey = errorKeys[0];

    if (firstKey === 'pattern') {
      if (field.label.toLowerCase().includes('nic')) {
        return 'Invalid NIC Number';
      }
      if (field.hint) {
        return field.hint;
      }
    }

    const message = VALIDATION_MESSAGES[firstKey];
    if (typeof message === 'function') {
      return message(errors[firstKey]);
    }
    if (message) return message;
    return 'Invalid field value';
  }

  isCurrentStepValid(): boolean {
    if (!this.employeeForm) return true;
    return this.getCurrentStepFields().every(field => {
      const control = this.employeeForm.get(field.key);
      return !control || control.disabled || control.valid;
    });
  }

  isFormValid = computed(() => {
    this.activeIndex();
    return this.employeeForm?.valid ?? false;
  });

  isLastStep = computed(() => this.activeIndex() === this.steps.length - 1);
  isFirstStep = computed(() => this.activeIndex() === 0);

  stepCompletionState = computed(() => {
    return this.steps.map((step, i) => {
      const fields = step.sections.flatMap(s => s.fields);
      const allValid = fields.every(f => {
        const ctrl = this.employeeForm?.get(f.key);
        return !ctrl || ctrl.disabled || ctrl.valid;
      });
      return {
        completed: allValid && (i < this.activeIndex() || i < this.maxVisitedStep()),
        hasError: !allValid && (i < this.activeIndex() || i < this.maxVisitedStep()),
        active: i === this.activeIndex()
      };
    });
  });

  // ─── Lifecycle ────────────────────────────────────────────────────────────────
  ngAfterViewInit(): void {}

  private scrollToTop(): void {
    const scrollContainer = document.querySelector('main.custom-scrollbar') as HTMLElement;
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  async ngOnInit(): Promise<void> {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.isEditMode.set(true);
      this.employeeId.set(idParam);
      this.pageTitleService.setTitle('Update Profile');
    } else {
      this.isEditMode.set(false);
      this.employeeId.set(null);
      this.pageTitleService.setTitle('Add a New Team Member');
    }

    try {
      // 1. Fetch (or return cached) form config
      const backendConfig = await this.registrationConfigService.ensureLoaded();

      // 2. Load API options dynamically from config endpoints
      await this.formOptions.loadOptionsFromConfig(backendConfig);

      // 3. Transform backend JSON → ModuleFormConfig
      const { config, submitUrl, hiddenFields, metadata } = this.adapter.transform(backendConfig, this.formOptions, this.isEditMode());
      this.formConfig.set(config);
      this._submitUrl.set(submitUrl);
      this._hiddenFields.set(hiddenFields);
      this._formMetadata.set(metadata);

      // 4. Build FormGroup from adapted config (re-enables 'role', disabled by default)
      this.employeeForm = this.payloadService.buildForm(config);

      // 4a. Add hidden fields as disabled controls so they appear in getRawValue()
      for (const key of hiddenFields) {
        this.employeeForm.addControl(key, new FormControl({ value: null, disabled: true }));
      }

      // 5. Wire up showWhen for depends_on fields
      this.adapter.applyDependencies(config, this.employeeForm, backendConfig);

      // 6. Apply default values for api-sourced selects
      this.formDefaults.applyApiDefaults(backendConfig, this.employeeForm, this.formOptions);

      if (this.isEditMode()) {
        const empId = this.employeeId();
        const baseUrl = (environment as any).baseUrl ?? environment.apiUrl;
        const res = await lastValueFrom(
          this.http.get<any>(`${baseUrl}/employees/${empId}`)
        );
        const employeeData = res?.data ?? res;
        this.patchEmployeeForm(employeeData);
      } else {
        // Restore draft ID if a previous draft exists
        const savedDraftId = this.storage?.getItem(this.DRAFT_ID_KEY);
        if (savedDraftId) this.draftEmployeeId.set(savedDraftId);
      }

      this.isLoading.set(false);

    } catch (err) {
      console.error('[EmployeeRegistration] Failed to load form config:', err);
      this.loadError.set('Failed to load registration form. Please try again.');
      this.isLoading.set(false);
    }
  }

  // ─── Navigation ──────────────────────────────────────────────────────────────
  next(): void {
    if (!this.isCurrentStepValid()) {
      this.markCurrentStepTouched();

      const invalidFields = this.getCurrentStepFields()
        .filter(field => {
          const control = this.employeeForm.get(field.key);
          return control && !control.disabled && control.invalid;
        });

      let detailMsg = 'Please complete all required fields and correct any invalid entries before proceeding.';
      if (invalidFields.length > 0) {
        const errorList = invalidFields.map(f => `${f.label}: ${this.getFieldError(f)}`);
        detailMsg = `Please correct the following: ${errorList.join(', ')}`;
      }

      this.uiOrchestrator.toast({
        severity: 'error',
        summary: 'Form Validation',
        detail: detailMsg
      });
      return;
    }
    const nextIdx = Math.min(this.activeIndex() + 1, this.steps.length - 1);
    this.activeIndex.set(nextIdx);
    if (nextIdx > this.maxVisitedStep()) {
      this.maxVisitedStep.set(nextIdx);
    }
    this.scrollToTop();
  }

  previous(): void {
    this.activeIndex.update(i => Math.max(i - 1, 0));
    this.scrollToTop();
  }

  goToStep(index: number): void {
    if (index <= this.maxVisitedStep()) {
      this.activeIndex.set(index);
      this.scrollToTop();
    }
  }

  private markCurrentStepTouched(): void {
    this.getCurrentStepFields().forEach(field => {
      this.employeeForm.get(field.key)?.markAsTouched();
    });
  }

  // ─── Submit ───────────────────────────────────────────────────────────────────
  isSubmitting = signal(false);

  formatPayloadForBackend(rawValue: any): any {
    const draftId = this.draftEmployeeId() || this.employeeId();
    return this.payloadService.formatPayloadForBackend(rawValue, this.formConfig(), this._formMetadata(), draftId);
  }

  private handleBackendValidationErrors(err: any): boolean {
    const errorBody = err?.error?.error;
    if (errorBody?.code === 'VALIDATION_ERROR' && errorBody?.steps) {
      const fieldErrors: Record<string, string> = {};
      const fields = this.getFieldsList();
      
      // Map field keys to their localized labels for the toast
      const fieldLabels = new Map<string, string>();
      for (const f of fields) {
        fieldLabels.set(f.key, f.label);
      }

      const toastErrors: string[] = [];
      const steps = errorBody.steps;
      
      for (const stepKey of Object.keys(steps)) {
        const stepFields = steps[stepKey];
        for (const fieldKey of Object.keys(stepFields)) {
          const details = stepFields[fieldKey];
          if (Array.isArray(details) && details.length > 0) {
            // Join all messages for this field if there are multiple rules failing (e.g. unique + expr)
            const messages = details.map((d: any) => d.message || 'Invalid value');
            const joinedMessage = messages.join(', ');
            fieldErrors[fieldKey] = joinedMessage;

            const label = fieldLabels.get(fieldKey) || fieldKey;
            toastErrors.push(`${label}: ${joinedMessage}`);
          }
        }
      }
      
      this.applyServerErrors(fieldErrors);

      let detailMsg = `${toastErrors.length} field${toastErrors.length !== 1 ? 's' : ''} failed validation. Please review and correct.`;
      if (toastErrors.length > 0 && toastErrors.length <= 4) {
        detailMsg = toastErrors.map(msg => msg.endsWith('.') ? msg : msg + '.').join(' ');
      }

      this.uiOrchestrator.toast({
        severity: 'error',
        summary: 'Validation Failed',
        detail: detailMsg
      });
      return true;
    }
    return false;
  }

  private extractErrorMessage(err: any, defaultMessage: string): string {
    if (!err) return defaultMessage;
    const body = err.error;
    if (!body) return err.message || defaultMessage;

    if (typeof body.message === 'string') return body.message;
    if (typeof body.error === 'string') return body.error;
    if (typeof body.error === 'object' && body.error?.message) return body.error.message;

    return defaultMessage;
  }

  onSubmit(): void {
    if (this.employeeForm.invalid) {
      this.employeeForm.markAllAsTouched();

      const fields = this.getFieldsList();
      const invalidFields = fields.filter(field => {
        const control = this.employeeForm.get(field.key);
        return control && !control.disabled && control.invalid;
      });

      let detailMsg = 'Please fill in all required fields and correct any invalid entries before submitting.';
      if (invalidFields.length > 0) {
        const errorList = invalidFields.map(f => `${f.label}: ${this.getFieldError(f)}`);
        detailMsg = `Please correct the following: ${errorList.join(', ')}`;
      }

      this.uiOrchestrator.toast({
        severity: 'error',
        summary: 'Validation Error',
        detail: detailMsg
      });
      return;
    }

    const rawValue = this.employeeForm.getRawValue();
    const payload = this.formatPayloadForBackend(rawValue);

    this.isSubmitting.set(true);

    const baseUrl = (environment as any).baseUrl ?? environment.apiUrl;

    if (this.isEditMode()) {
      const empId = this.employeeId();
      this.http.put<any>(`${baseUrl}/employees/${empId}`, payload).subscribe({
        next: (res: any) => {
          this.isSubmitting.set(false);
          const displayName = this.employeeForm.get('full_name')?.value || this.employeeForm.get('name')?.value || 'Employee';
          if (res?.pendingChanges && res.pendingChanges.length > 0) {
            this.uiOrchestrator.toast({
              severity: 'info',
              summary: 'Changes Saved',
              detail: 'Updates applied. Sensitive changes are pending approval.'
            });
          } else {
            this.uiOrchestrator.toast({
              severity: 'success',
              summary: 'Changes Saved',
              detail: `${displayName}'s profile has been successfully updated.`
            });
          }
          this.router.navigate(['/employees']);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          console.error('[EmployeeRegistration] Save changes failed:', err);
          if (this.handleBackendValidationErrors(err)) return;
          this.uiOrchestrator.toast({
            severity: 'error',
            summary: 'Save Failed',
            detail: this.extractErrorMessage(err, 'Could not save changes. Please try again.')
          });
        }
      });
    } else {
      this.http.post(`${baseUrl}/employees`, payload).subscribe({
        next: () => {
          this.isSubmitting.set(false);
          this.storage?.removeItem(this.DRAFT_KEY);
          this.storage?.removeItem(this.DRAFT_ID_KEY);
          this.draftEmployeeId.set(null);
          const displayName = this.employeeForm.get('full_name')?.value || this.employeeForm.get('name')?.value || 'Person';
          this.uiOrchestrator.toast({
            severity: 'success',
            summary: 'Registration Submitted',
            detail: `${displayName} has been successfully registered.`
          });
          this.router.navigate(['/employees']);
        },
        error: (err) => {
          this.isSubmitting.set(false);
          console.error('[EmployeeRegistration] Submit failed:', err);
          if (this.handleBackendValidationErrors(err)) return;
          this.uiOrchestrator.toast({
            severity: 'error',
            summary: 'Submission Failed',
            detail: this.extractErrorMessage(err, 'Could not submit registration. Please try again.')
          });
        }
      });
    }
  }

  private applyServerErrors(errors: Record<string, string>): void {
    for (const [key, message] of Object.entries(errors)) {
      const ctrl = this.employeeForm.get(key);
      if (ctrl) {
        ctrl.setErrors({ serverError: message });
        ctrl.markAsTouched();
      }
    }
  }

  onCancel(): void {
    this.router.navigate(['/employees']);
  }

  onSaveDraft(): void {
    if (!this.canSaveDraft()) {
      this.uiOrchestrator.toast({
        severity: 'warn',
        summary: 'Full Name Required',
        detail: "Please enter the employee's Full Name before saving a draft."
      });
      return;
    }
    const formValue = this.employeeForm.getRawValue();
    const payload = this.formatPayloadForBackend(formValue);

    this.isSavingDraft.set(true);
    const baseUrl = (environment as any).baseUrl ?? environment.apiUrl;
    this.http.post<any>(`${baseUrl}/employees/draft`, payload).subscribe({
      next: (res: any) => {
        const responseData = res?.data ?? res;
        const draftId = responseData?.employee_id || responseData?.id || responseData?.draftId;
        this.isSavingDraft.set(false);
        this.draftEmployeeId.set(draftId);
        this.storage?.setItem(this.DRAFT_ID_KEY, String(draftId));
        this.storage?.setItem(this.DRAFT_KEY, JSON.stringify(formValue));
        this.uiOrchestrator.toast({
          severity: 'success',
          summary: 'Draft Saved',
          detail: 'Your progress has been saved and recorded. You can continue later.'
        });
        this.router.navigate(['/employees']);
      },
      error: (err) => {
        this.isSavingDraft.set(false);
        console.error('[EmployeeRegistration] Save draft failed:', err);
        this.uiOrchestrator.toast({
          severity: 'error',
          summary: 'Save Failed',
          detail: this.extractErrorMessage(err, 'Could not save draft. Please try again.')
        });
      }
    });
  }


  // ─── Review Screen Helper (Removed reviewSections, delegated to DynamicViewRendererComponent) ───

  patchEmployeeForm(data: any): void {
    this.payloadService.patchEmployeeForm(this.employeeForm, data, this.formConfig());
  }

  private getFieldsList(): FormFieldConfig[] {
    return this.payloadService.getFieldsList(this.formConfig());
  }

  formatDate(value: any): Date | null {
    return this.payloadService.formatDate(value);
  }

  formatFormValuesForPayload(data: any): any {
    return this.payloadService.formatFormValuesForPayload(data, this.formConfig());
  }

  retryLoad(): void {
    this.isLoading.set(true);
    this.loadError.set(null);
    this.activeIndex.set(0);
    this.maxVisitedStep.set(0);
    this.ngOnInit();
  }
}
